import { useState, useEffect, useCallback } from 'react';
import { ProcedureCodeEstimate, ProcedureType as ProcedureCodeType } from '../../../../../../../services/ContractService/types/contractEstimateTypes';
import { ProcedureType } from '../../../../../../../services/ConfigurationService/types/procedureType';

export interface ProcedureFormData {
    priority: number;
    units: number;
    implantCost: number;
    selectedProcedure: ProcedureType | null;
    // Calculator-specific fields
    procedureType: ProcedureCodeType;
    billedCharges: number;
    revenueCode: string;
    modifiers: string[];
    apcCode: string;
}

export interface ProcedureFormErrors {
    priority?: string;
    units?: string;
    implantCost?: string;
    selectedProcedure?: string;
    billedCharges?: string;
    revenueCode?: string;
}

const INITIAL_FORM_DATA: ProcedureFormData = {
    priority: 1,
    units: 1,
    implantCost: 0,
    selectedProcedure: null,
    procedureType: 'CPT',
    billedCharges: 0,
    revenueCode: '',
    modifiers: [],
    apcCode: '',
};

interface UseEstProcedureFormProps {
    editingProcedure: ProcedureCodeEstimate | null;
    availableProcedures: ProcedureType[];
    open: boolean;
}

interface UseEstProcedureFormResult {
    formData: ProcedureFormData;
    formErrors: ProcedureFormErrors;
    setFormData: React.Dispatch<React.SetStateAction<ProcedureFormData>>;
    handleFormChange: (field: keyof ProcedureFormData, value: any) => void;
    handleProcedureSelect: (procedure: ProcedureType) => void;
    clearSelectedProcedure: () => void;
    validateForm: () => boolean;
    clearSubmitError: () => void;
}

export const useEstProcedureForm = ({
    editingProcedure,
    availableProcedures,
    open
}: UseEstProcedureFormProps): UseEstProcedureFormResult => {
    const [formData, setFormData] = useState<ProcedureFormData>(INITIAL_FORM_DATA);
    const [formErrors, setFormErrors] = useState<ProcedureFormErrors>({});

    // Initialize form when dialog opens or editing procedure changes
    useEffect(() => {
        if (open) {
            if (editingProcedure) {
                // For editing, find the matching procedure from available procedures
                const matchingProcedure = availableProcedures.find(proc =>
                    proc.code === editingProcedure.code
                );

                setFormData({
                    priority: editingProcedure.priority,
                    units: editingProcedure.units,
                    implantCost: editingProcedure.implantCost,
                    selectedProcedure: matchingProcedure || null,
                    procedureType: editingProcedure.procedureType || 'CPT',
                    billedCharges: editingProcedure.billedCharges || 0,
                    revenueCode: editingProcedure.revenueCode || '',
                    modifiers: editingProcedure.modifiers || [],
                    apcCode: editingProcedure.apcCode || '',
                });
            } else {
                // Create new procedure with default values
                setFormData(INITIAL_FORM_DATA);
            }
            setFormErrors({});
        }
    }, [open, editingProcedure, availableProcedures]);

    const handleFormChange = useCallback((field: keyof ProcedureFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        setFormErrors(prev => ({
            ...prev,
            [field]: undefined,
        }));
    }, []);

    const handleProcedureSelect = useCallback((procedure: ProcedureType) => {
        setFormData(prev => ({
            ...prev,
            selectedProcedure: procedure,
        }));

        // Clear error when user selects a procedure
        setFormErrors(prev => ({
            ...prev,
            selectedProcedure: undefined,
        }));
    }, []);

    const clearSelectedProcedure = useCallback(() => {
        setFormData(prev => ({ ...prev, selectedProcedure: null }));
    }, []);

    const validateForm = useCallback((): boolean => {
        const errors: ProcedureFormErrors = {};

        if (formData.priority < 1) {
            errors.priority = 'Priority must be at least 1';
        }

        if (!formData.selectedProcedure) {
            errors.selectedProcedure = 'Please select a procedure code';
        }

        if (formData.units <= 0) {
            errors.units = 'Units must be greater than 0';
        }

        if (formData.implantCost < 0) {
            errors.implantCost = 'Implant cost cannot be negative';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData]);

    const clearSubmitError = useCallback(() => {
        setFormErrors({});
    }, []);

    return {
        formData,
        formErrors,
        setFormData,
        handleFormChange,
        handleProcedureSelect,
        clearSelectedProcedure,
        validateForm,
        clearSubmitError
    };
};
